import serverContext from 'server-only-context';
import { isDraftPreviewEnabled } from 'utils/draftPreview';
import { NinetailedMode } from 'types/constants';
import { isE2ETesting } from 'utils/environment';
import { PageContext } from 'utils/pageContext';
import { isExperimentPipelineV2Enabled } from 'libs/statsig';
import { decodeExperienceSelections } from 'libs/experimentation/ninetailed-proxy';

export const [getExperiences, setExperiences] = serverContext<Record<string, number> | null>(null);

export const getNinetailedMode: () => Promise<NinetailedMode> = async () => {
  // We use this to override the mode when needed
  // It's handy for local development in .env.local
  // or if we need to override and entire environment like QA
  const overrideMode = process.env.NEXT_PUBLIC_NINETAILED_MODE;

  // If an override mode is set and valid, use it
  if (overrideMode === 'server' || overrideMode === 'client') {
    return overrideMode;
  }

  // Otherwise determine the rendering mode based on whether the env enables draft preview
  return (await isDraftPreviewEnabled()) ? 'client' : 'server';
};

export async function getSlugAndExperiences({ params }: { params: Promise<{ slug: string[] }> }) {
  const rawSlug = (await params)?.slug || [];
  const slug = rawSlug.join('/');

  // importing headers here in-line allows
  // this function to be used both client + server-side
  const { headers } = await import('next/headers');
  const headersList = await headers();

  // Get experience selections from header
  const experienceSelections = headersList.get('x-ninetailed-experiences');
  const selectedExperiences = experienceSelections ? decodeExperienceSelections(experienceSelections) : null;

  return {
    selectedExperiences,
    slug,
  };
}

/**
 * Resolves per-request experimentation state for a page entry.
 *
 * Reads the slug from `params`, decodes the Ninetailed experience selections
 * from the proxy header, stores them in the request-scoped context, and
 * returns the resolved Ninetailed render mode alongside the slug.
 */
export async function setupRequestExperiences({
  params,
}: {
  params: Promise<{ slug: string[] }>;
}): Promise<{ slug: string; ninetailedMode: NinetailedMode }> {
  const ninetailedMode = await getNinetailedMode();
  const { slug, selectedExperiences } = await getSlugAndExperiences({ params });
  setExperiences(selectedExperiences);
  return { slug, ninetailedMode };
}

/**
 * Whether Statsig experiments are enabled for this page.
 * Currently only the referral page (/r/...) uses Statsig for hero experiments.
 *
 * To be removed when Experiment Pipeline V2 is fully rolled out.
 */
export async function isStatsigExperiment(headers?: PageContext): Promise<boolean> {
  if (headers == undefined || headers?.anonymousId == undefined) {
    return false;
  }
  // V2 pipeline uses per-module type guards (hasStatsigContainer) instead of URL routing
  if (await isExperimentPipelineV2Enabled()) {
    return false;
  }
  return headers.url.includes('/r/');
}

/**
 * Whether Ninetailed experiments are enabled for this page.
 * Ninetailed is always enabled. It coexists with Statsig on pages that use both.
 */
export async function isNinetailedExperiment(_headers?: PageContext): Promise<boolean> {
  return true;
}

/**
 * Determines whether to force baseline rendering for Ninetailed experiences.
 * This function centralizes all conditions that should force baseline rendering.
 */
export function shouldRenderBaseline(): boolean {
  // Always render baseline in CI environments to prevent flaky tests or unintended VRT failures
  if (isE2ETesting()) {
    return true;
  }

  // Future conditions can be added here:
  // - Feature flags (e.g., disable personalization for certain users)
  // - User preferences (e.g., opt-out of personalization)
  // - Performance considerations (e.g., disable on slow connections)

  return false;
}
